import { ref, computed } from 'vue'
import servoList from '../servoList.json'
import { calculateInverseKinematics, calculateRelativeAngle } from '../utils/kinematics.js'
import {
  isNestedTrajectory,
  findPersonConfigByPersonName,
  findPersonIdByPersonName,
  findPersonConfigByPersonId,
  getOriginPosition,
  swapLeftRightInServoName
} from '../utils/trajectoryConfig.js'

// 镜像轨迹与返程轨迹
// 外部依赖：
//   trajectoryConfigs / trajectoryListData / currentTrajectoryId / servoVersion - Ref
//   getSelectedDeviceServoVersion - 获取当前设备舵机型号的函数
//   getAngleForServoIndex         - 根据舵机索引取对应 A/B 角度的函数 (trajectory, idx, angles) => number
//   addServoMotionForTrajectoryPoint - 为轨迹点添加舵机角度片段的函数 (trajectory, pointIdx, steps) => void
//   showSnackbar - 显示提示信息的回调 (msg: string) => void
export function useMirrorTrajectory(deps) {
  const {
    trajectoryConfigs, trajectoryListData, currentTrajectoryId, servoVersion,
    getSelectedDeviceServoVersion, getAngleForServoIndex, addServoMotionForTrajectoryPoint,
    showSnackbar
  } = deps

  // 镜像轨迹相关
  const mirrorTrajectoryDialog = ref(false)
  const mirrorTrajectoryId = ref(null)
  const mirrorTargetTrajectoryName = ref(null)

  // 添加返程相关
  const addReturnTripDialog = ref(false)
  const addReturnTripTrajectoryId = ref(null)

  // 获取镜像源轨迹（用于对话框显示源轨迹信息）
  const getSourceTrajectoryForMirror = computed(() => {
    if (!mirrorTrajectoryId.value) return null
    return trajectoryConfigs.value.find(t => t.id === mirrorTrajectoryId.value)
  })

  // 获取镜像人物提示信息
  function getMirrorPersonHint(trajectoryName, trajectoryGroup) {
    if (!mirrorTrajectoryId.value) return ''
    const sourceTrajectory = trajectoryConfigs.value.find(t => t.id === mirrorTrajectoryId.value)
    if (!sourceTrajectory || !sourceTrajectory.personName) return ''

    // 如果目标轨迹是嵌套结构且包含源轨迹的人物名称，显示提示
    if (isNestedTrajectory(trajectoryGroup)) {
      const foundConfig = findPersonConfigByPersonName(trajectoryGroup, sourceTrajectory.personName)
      if (foundConfig) {
        return `将使用: ${sourceTrajectory.personName}`
      }
    }
    return ''
  }

  /** 根据源轨迹各舵机在 servoList 中的名称，解析镜像侧舵机（与当前设备型号一致） */
  function buildMirrorTargetServosFromSource(sourceTrajectory) {
    const vers =
      getSelectedDeviceServoVersion() ||
      (servoVersion.value && servoList[servoVersion.value] && servoVersion.value) ||
      (sourceTrajectory.config?.servoVersions || []).find(v => servoList[v])
    if (!vers || !sourceTrajectory.servos?.length) return null
    const list = servoList[vers]
    const out = []
    for (const s of sourceTrajectory.servos) {
      const mirroredName = swapLeftRightInServoName(s.name)
      const info = list.find(item => item.name === mirroredName)
      if (!info) return null
      out.push({
        s: info.id,
        name: info.name,
        range: info.range || [-200, 200],
        l: s.l || 1,
        m: []
      })
    }
    return out.length === sourceTrajectory.servos.length ? out : null
  }

  function applyMirrorTargetServosFromSource(sourceTrajectory, mirrorTargetName, targetTrajectory) {
    const isMouthPair =
      (sourceTrajectory.trajectoryName === '右嘴角' && mirrorTargetName === '左嘴角') ||
      (sourceTrajectory.trajectoryName === '左嘴角' && mirrorTargetName === '右嘴角')
    if (!isMouthPair) return
    const built = buildMirrorTargetServosFromSource(sourceTrajectory)
    if (!built) return
    targetTrajectory.servos = built
    if (targetTrajectory.config) {
      targetTrajectory.config.ids = built.map(x => x.s)
      const dv = getSelectedDeviceServoVersion()
      if (dv && servoList[dv]) {
        const sv = sourceTrajectory.config?.servoVersions || []
        targetTrajectory.config.servoVersions = [...new Set([dv, ...sv].filter(Boolean))]
      }
    }
  }

  // 打开镜像轨迹对话框
  function openMirrorTrajectoryDialog(trajectoryId) {
    mirrorTrajectoryId.value = trajectoryId
    mirrorTargetTrajectoryName.value = null

    // 如果源轨迹有轨迹名称，尝试找到对应的镜像目标（例如：右嘴角 -> 左嘴角）
    const sourceTrajectory = trajectoryConfigs.value.find(t => t.id === trajectoryId)
    if (sourceTrajectory && sourceTrajectory.trajectoryName) {
      // 尝试找到镜像轨迹（简单的左右对应关系）
      const sourceName = sourceTrajectory.trajectoryName
      let mirrorName = null

      // 常见的镜像对应关系
      if (sourceName === '右嘴角') {
        mirrorName = '左嘴角'
      } else if (sourceName === '左嘴角') {
        mirrorName = '右嘴角'
      }

      // 如果找到了镜像轨迹名称，且该轨迹存在，则默认选中
      if (mirrorName && trajectoryListData.value[mirrorName]) {
        mirrorTargetTrajectoryName.value = mirrorName
      }
    }

    mirrorTrajectoryDialog.value = true
  }

  // 执行镜像轨迹
  function performMirrorTrajectory() {
    if (!mirrorTrajectoryId.value || !mirrorTargetTrajectoryName.value) {
      showSnackbar('请选择镜像目标')
      return
    }

    const mirrorTargetNameSaved = mirrorTargetTrajectoryName.value

    const sourceTrajectory = trajectoryConfigs.value.find(t => t.id === mirrorTrajectoryId.value)
    if (!sourceTrajectory) {
      showSnackbar('找不到源轨迹')
      return
    }

    // 获取目标轨迹配置
    const trajectoryGroup = trajectoryListData.value[mirrorTargetTrajectoryName.value]
    if (!trajectoryGroup) {
      showSnackbar('找不到目标轨迹配置')
      return
    }

    // 确定目标配置：如果是嵌套结构，优先使用源轨迹的 personId，否则使用 personName，最后使用第一个
    let targetConfig = null
    let targetPersonName = ''
    let targetPersonId = null
    if (isNestedTrajectory(trajectoryGroup)) {
      // 嵌套结构：优先使用源轨迹的 personId（数字键）
      if (sourceTrajectory.personId && trajectoryGroup[sourceTrajectory.personId]) {
        targetConfig = findPersonConfigByPersonId(trajectoryGroup, sourceTrajectory.personId)
        if (targetConfig) {
          targetPersonId = sourceTrajectory.personId
          targetPersonName = targetConfig.personName || ''
        }
      } else if (sourceTrajectory.personName) {
        // 如果没有 personId，使用 personName 查找
        targetConfig = findPersonConfigByPersonName(trajectoryGroup, sourceTrajectory.personName)
        if (targetConfig) {
          targetPersonName = sourceTrajectory.personName
          targetPersonId = findPersonIdByPersonName(trajectoryGroup, sourceTrajectory.personName)
        }
      }
      // 如果没有找到对应的人物，使用第一个
      if (!targetConfig) {
        const firstKey = Object.keys(trajectoryGroup)[0]
        if (firstKey) {
          targetConfig = trajectoryGroup[firstKey]
          targetPersonId = firstKey
          targetPersonName = targetConfig?.personName || ''
        }
      }
    } else {
      // 旧格式，直接使用
      targetConfig = trajectoryGroup
    }

    if (!targetConfig) {
      showSnackbar('找不到目标轨迹配置')
      return
    }

    // 构建目标轨迹的完整名称
    const targetFullName = targetPersonName ? `${mirrorTargetTrajectoryName.value}-${targetPersonName}` : mirrorTargetTrajectoryName.value

    // 检查目标轨迹是否已存在
    const existingIndex = trajectoryConfigs.value.findIndex(t => t.name === targetFullName)

    let targetTrajectory
    if (existingIndex >= 0) {
      // 如果已存在，使用现有轨迹
      targetTrajectory = trajectoryConfigs.value[existingIndex]
    } else {
      // 如果不存在，创建新轨迹
      const newId = Date.now()
      const initialPosition = getOriginPosition(targetConfig, 'origin')
      const initialX = initialPosition.x
      const initialY = initialPosition.y

      targetTrajectory = {
        id: newId,
        name: targetFullName,
        trajectoryName: mirrorTargetTrajectoryName.value,
        personName: targetPersonName || '',
        personId: targetPersonId || '',
        config: {
          name: targetFullName,
          trajectoryName: mirrorTargetTrajectoryName.value,
          personName: targetPersonName || '',
          personId: targetPersonId || '',
          ...targetConfig
        },
        servos: [],
        armConfig: { servoA: 0, servoB: 0 },
        trajectoryX: initialX,
        trajectoryY: initialY,
        trajectoryPoints: [{x: initialX, y: initialY}],
        zeroPositionAngles: { servoA: 0, servoB: 0 }
      }

      // 计算零位角度（从 origin 零位计算）
      if (targetConfig.armLengths && targetConfig.servoDistance) {
        const zeroPosition = getOriginPosition(targetConfig, 'origin')
        const zeroAngles = calculateInverseKinematics(
          zeroPosition.x,
          zeroPosition.y,
          targetConfig.armLengths,
          targetConfig.servoDistance
        )
        if (zeroAngles) {
          targetTrajectory.zeroPositionAngles.servoA = zeroAngles.servoA
          targetTrajectory.zeroPositionAngles.servoB = zeroAngles.servoB

          // 计算初始位置相对于零位的偏移
          const initialAngles = calculateInverseKinematics(
            targetTrajectory.trajectoryX,
            targetTrajectory.trajectoryY,
            targetConfig.armLengths,
            targetConfig.servoDistance
          )
          if (initialAngles) {
            targetTrajectory.armConfig.servoA = calculateRelativeAngle(initialAngles.servoA, zeroAngles.servoA)
            targetTrajectory.armConfig.servoB = calculateRelativeAngle(initialAngles.servoB, zeroAngles.servoB)
          } else {
            targetTrajectory.armConfig.servoA = 0
            targetTrajectory.armConfig.servoB = 0
          }
        }
      }

      // 初始化舵机数据
      if (targetConfig.ids && targetConfig.ids.length > 0) {
        const servoIds = targetConfig.ids
        const servoVersions = targetConfig.servoVersions || []

        targetTrajectory.servos = servoIds.map((id) => {
          let servoInfo = null
          for (const version of servoVersions) {
            if (servoList[version]) {
              servoInfo = servoList[version].find(s => s.id === id)
              if (servoInfo) break
            }
          }

          return {
            s: id,
            name: servoInfo ? servoInfo.name : `舵机${id}`,
            range: servoInfo ? servoInfo.range : [-200, 200],
            l: 1,
            m: []
          }
        })
      }

      trajectoryConfigs.value.push(targetTrajectory)
    }

    // 镜像侧舵机号与当前设备一致：按源舵机名称左右互换后在 servoList 中解析（避免沿用 trajectoryList 里其它型号的 ids）
    applyMirrorTargetServosFromSource(sourceTrajectory, mirrorTargetNameSaved, targetTrajectory)

    // 执行镜像：复制源轨迹的所有轨迹点，x坐标取反，y坐标和步数保持不变
    if (sourceTrajectory.trajectoryPoints && sourceTrajectory.trajectoryPoints.length > 0) {
      // 复制轨迹点（x取反，y保持不变）
      const mirroredPoints = sourceTrajectory.trajectoryPoints.map(point => ({
        x: -point.x,
        y: point.y
      }))

      // 替换目标轨迹的轨迹点
      targetTrajectory.trajectoryPoints = mirroredPoints

      // 同时更新trajectoryX和trajectoryY（保持向后兼容）
      if (mirroredPoints.length > 0) {
        targetTrajectory.trajectoryX = mirroredPoints[0].x
        targetTrajectory.trajectoryY = mirroredPoints[0].y
      }

      // 重新计算舵机角度片段
      if (targetTrajectory.config.armLengths && targetTrajectory.config.servoDistance &&
          sourceTrajectory.servos && sourceTrajectory.servos.length > 0 &&
          targetTrajectory.servos && targetTrajectory.servos.length > 0) {

        // 为每个目标舵机重新计算角度片段
        for (let idx = 0; idx < Math.min(sourceTrajectory.servos.length, targetTrajectory.servos.length); idx++) {
          const sourceServo = sourceTrajectory.servos[idx]
          const targetServo = targetTrajectory.servos[idx]

          // 清除原有的角度片段
          targetServo.m = []

          // 为每个轨迹点之间的片段计算舵机角度
          if (mirroredPoints.length >= 2) {
            for (let i = 0; i < mirroredPoints.length - 1; i++) {
              const startPoint = mirroredPoints[i]
              const endPoint = mirroredPoints[i + 1]

              // 获取源轨迹对应的角度片段
              const sourceMotion = sourceServo.m[i]
              if (!sourceMotion) continue

              // 计算起点和终点的舵机角度（相对于零位）
              let startAnglesA = 0, startAnglesB = 0
              let endAnglesA = 0, endAnglesB = 0

              // 计算起点的舵机角度
              const startAngles = calculateInverseKinematics(
                startPoint.x,
                startPoint.y,
                targetTrajectory.config.armLengths,
                targetTrajectory.config.servoDistance
              )
              if (startAngles && targetTrajectory.zeroPositionAngles) {
                startAnglesA = calculateRelativeAngle(startAngles.servoA, targetTrajectory.zeroPositionAngles.servoA)
                startAnglesB = calculateRelativeAngle(startAngles.servoB, targetTrajectory.zeroPositionAngles.servoB)
              }

              // 计算终点的舵机角度
              const endAngles = calculateInverseKinematics(
                endPoint.x,
                endPoint.y,
                targetTrajectory.config.armLengths,
                targetTrajectory.config.servoDistance
              )
              if (endAngles && targetTrajectory.zeroPositionAngles) {
                endAnglesA = calculateRelativeAngle(endAngles.servoA, targetTrajectory.zeroPositionAngles.servoA)
                endAnglesB = calculateRelativeAngle(endAngles.servoB, targetTrajectory.zeroPositionAngles.servoB)
              }

              // 使用源轨迹的步数
              const steps = sourceMotion.steps

              // 使用线性插值计算1/3和2/3点的角度（或者使用最小二乘法拟合）
              const startAngle = getAngleForServoIndex(targetTrajectory, idx, { servoA: startAnglesA, servoB: startAnglesB })
              const endAngle = getAngleForServoIndex(targetTrajectory, idx, { servoA: endAnglesA, servoB: endAnglesB })

              const thirdPoint = Number((startAngle + (endAngle - startAngle) / 3).toFixed(1))
              const twoThirdPoint = Number((startAngle + 2 * (endAngle - startAngle) / 3).toFixed(1))

              // 创建角度片段
              const motion = {
                steps: steps,
                param: [startAngle, endAngle, thirdPoint, twoThirdPoint]
              }

              targetServo.m.push(motion)
            }
          }
        }
      }
    }

    // 关闭对话框
    mirrorTrajectoryDialog.value = false
    mirrorTrajectoryId.value = null
    mirrorTargetTrajectoryName.value = null

    // 显示成功消息
    showSnackbar(`已成功将轨迹镜像到"${mirrorTargetNameSaved}"`)

    // 如果目标是新轨迹，切换到该轨迹
    if (existingIndex < 0) {
      currentTrajectoryId.value = targetTrajectory.id
    }
  }

  // 打开添加返程对话框
  function openAddReturnTripDialog(trajectoryId) {
    addReturnTripTrajectoryId.value = trajectoryId
    addReturnTripDialog.value = true
  }

  // 确认添加返程
  function confirmAddReturnTrip() {
    if (!addReturnTripTrajectoryId.value) {
      addReturnTripDialog.value = false
      return
    }

    const trajectory = trajectoryConfigs.value.find(t => t.id === addReturnTripTrajectoryId.value)
    if (!trajectory) {
      showSnackbar('找不到轨迹')
      addReturnTripDialog.value = false
      return
    }

    // 确保trajectoryPoints数组存在
    if (!trajectory.trajectoryPoints) {
      trajectory.trajectoryPoints = [{x: trajectory.trajectoryX, y: trajectory.trajectoryY}]
    }

    const points = trajectory.trajectoryPoints

    // 检查是否有足够的轨迹点
    if (points.length < 2) {
      showSnackbar('至少需要2个轨迹点才能添加返程')
      addReturnTripDialog.value = false
      return
    }

    // 记录当前轨迹点数量
    const currentPointCount = points.length

    // 添加返程点：从倒数第二个点开始，逆序添加到第一个点
    // 例如：3个点[A,B,C] -> 添加[B,A]
    //      4个点[A,B,C,D] -> 添加[C,B,A]
    //      5个点[A,B,C,D,E] -> 添加[D,C,B,A]
    for (let i = currentPointCount - 2; i >= 0; i--) {
      const point = points[i]
      trajectory.trajectoryPoints.push({x: point.x, y: point.y})
    }

    // 为新添加的所有返程点添加对应的舵机角度片段
    // 返程的步数应该使用对应正向片段的步数（逆序）
    for (let i = currentPointCount; i < trajectory.trajectoryPoints.length; i++) {
      // 计算对应的原始片段索引
      // 返程点索引i对应的原始点索引是 currentPointCount - 2 - (i - currentPointCount)
      // 也就是 currentPointCount - 2 - i + currentPointCount = 2 * currentPointCount - 2 - i
      // 返程片段从点i-1到点i，对应原始片段从点(2*currentPointCount-2-i)到点(2*currentPointCount-1-i)
      // 原始片段索引是 (2*currentPointCount-1-i) - 1 = 2*currentPointCount-2-i
      const originalSegmentIdx = 2 * currentPointCount - 2 - i

      // 获取对应原始片段的步数
      let returnSteps = 0 // 默认值
      if (trajectory.servos && trajectory.servos.length > 0 &&
          trajectory.servos[0].m && trajectory.servos[0].m[originalSegmentIdx]) {
        returnSteps = trajectory.servos[0].m[originalSegmentIdx].steps
      }

      addServoMotionForTrajectoryPoint(trajectory, i, returnSteps)
    }

    // 关闭对话框
    addReturnTripDialog.value = false
    addReturnTripTrajectoryId.value = null

    // 显示成功消息
    showSnackbar('已成功添加返程轨迹')
  }

  return {
    mirrorTrajectoryDialog,
    mirrorTrajectoryId,
    mirrorTargetTrajectoryName,
    addReturnTripDialog,
    addReturnTripTrajectoryId,
    getSourceTrajectoryForMirror,
    getMirrorPersonHint,
    openMirrorTrajectoryDialog,
    performMirrorTrajectory,
    openAddReturnTripDialog,
    confirmAddReturnTrip
  }
}
